import { FC, useEffect, useState } from "react";
import { styled } from "styled-components";
import { colors, font } from "../lib/sharedStyles";

const SwatchContainer = styled.div<{ $layout: string; $isHoverInfo: boolean; }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 8px;
  @media (min-width: 1024px) {
    ${({ $layout, $isHoverInfo }) => $layout == 'grid' && !$isHoverInfo && `
      display: none;
    `}
    ${({ $layout }) => $layout == 'list' && `
      order: 1;
      margin-bottom: 0;
    `}
  }
`;

const SwatchLabel = styled.p<{ $layout: string; }>`
  display: none;
  width: 100%;
  margin: 16px 0 8px;
  ${font('Instrument Sans', 400, '12px', '18px')}
  letter-spacing: 0.12px;
  color: ${colors.fender.primaryDark};
  span {
    font-weight: 600;
  }
  @media (min-width: 1024px) {
    ${({ $layout }) => $layout == 'list' && `
      display: block;
    `}
  }
`;

const SwatchButtonWrap = styled.div`
  display: flex;
  gap: 4px;
`;

const SwatchButton = styled.button<{ $isActive?: boolean; }>`
  appearance: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 20px;
  // Todo: reset issue SearchspringPLP PageContainer
  border: 1px solid rgba(25,26,31,0.2) !important;
  ${({ $isActive }) => $isActive && `
    border: 1px solid rgb(25,26,31) !important;
  `}
  > div {
    display: flex;
    overflow: hidden;
    width: 14px;
    height: 14px;
    border-radius: 14px;
  }
`;

const AdditionalSwatchCount = styled.p`
  margin: 0 0 0 4px;
  ${font('Instrument Sans', 400, '12px', '18px')}
  letter-spacing: 0.12px;
  color: ${colors.fender.grey04};
`;

interface SearchspringTileSwatchesProps {
  activeVariantIndex: number;
  // Todo: this is a little clumsy.
  isHoverInfo?: boolean;
  layout: string;
  variants: {name?: string; url?: string}[];
  productHandle: string;
  onSwatchHover: (index: number) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const SearchspringTileSwatches: FC<SearchspringTileSwatchesProps> = ({
  activeVariantIndex,
  isHoverInfo = false,
  layout,
  variants,
  productHandle,
  onSwatchHover,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [swatchLimit, setSwatchLimit] = useState<number>(3);

  const isFmicAssetsImage = (url: string): boolean => {
    return url.includes('www.fmicassets.com');
  };

  const getResizedImageUrl = (url: string): string => {
    return isFmicAssetsImage(url) ? `https://www.fender.com/cdn-cgi/image/format=png,resize=height=auto,width=50/${url}` : `${url}`;
  };

  useEffect(() => {
    const updateSwatchLimit = () => {
      setSwatchLimit(window.innerWidth >= 375 ? 5 : 3);
    };

    updateSwatchLimit();
    window.addEventListener('resize', updateSwatchLimit);

    return () => {
      window.removeEventListener('resize', updateSwatchLimit);
    };
  }, []);

  return (
    <SwatchContainer $layout={layout} $isHoverInfo={isHoverInfo}>
      <SwatchLabel $layout={layout}>
        <span>Color: </span>
        {variants[activeVariantIndex]?.name}
      </SwatchLabel>
      <SwatchButtonWrap onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {variants.slice(0, swatchLimit).map(({ url }, index) => (
          url && (
            <SwatchButton
              key={`${productHandle}-${index}-${url}`}
              $isActive={index === activeVariantIndex}
              onMouseEnter={() => onSwatchHover(index)}
            >
              <div>
                <img loading="lazy" src={getResizedImageUrl(url)} alt={`Swatch ${index}`} />
              </div>
            </SwatchButton>
          )
        ))}
      </SwatchButtonWrap>
      {(variants.length > swatchLimit) && (
        <AdditionalSwatchCount>+ {variants.length - swatchLimit}</AdditionalSwatchCount>
      )}
    </SwatchContainer>
  );
}

export default SearchspringTileSwatches;
